import { useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useScreen } from '../../lib/layout.ts';
import { useLayer } from '../../lib/modal-focus.ts';
import { cn } from '../../lib/utils.ts';
import { Sheet } from '../sheet.tsx';

/**
 * Popover (shadcn-Verhalten, DESIGN „Spaltenwahl“ / Filter-Auswahl): weiße Karte mit weichem Schatten unter dem
 * auslösenden Knopf. Schließt bei Klick daneben und mit Esc; danach Fokus zurück auf den Knopf. Am Handy (unter 768 px)
 * öffnet derselbe Inhalt als Blatt von unten (EXPERIENCE §10.2) mit Abdunklung und Fokusfalle.
 */
export function Popover({
  open,
  onOpenChange,
  trigger,
  children,
  label,
  align = 'start',
  className,
  fixed = false,
  autoFocus = true,
  passive = false,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Knopf; bekommt `aria-expanded`, `aria-controls` und den Klick zum Öffnen. */
  trigger: (props: { 'aria-expanded': boolean; 'aria-controls': string | undefined; onClick: () => void }) => ReactNode;
  children: ReactNode;
  /** Name des Dialogs für Hilfsmittel (am Handy auch Überschrift des Blatts). */
  label: string;
  align?: 'start' | 'end';
  className?: string;
  /**
   * Fest am Bildschirm statt im Fluss (z. B. im Spaltenkopf einer waagerecht scrollbaren Tabelle, deren Rand das Popover
   * sonst abschneidet): Lage aus dem Knopf, beim Scrollen und bei Größenänderung nachgeführt, im Fenster gehalten.
   */
  fixed?: boolean;
  /**
   * Am PC/Tablet beim Öffnen den Fokus in das erste Element setzen (Standard). `false`: Fokus bleibt, wo er ist – z. B.
   * Klick ins Datumsfeld öffnet den Kalender, getippt wird weiter im Feld (Ticket 1.215). Am Handy übernimmt immer das Blatt.
   */
  autoFocus?: boolean;
  /**
   * Nur angezeigt, nicht bedient (z. B. per Maus geöffnete Infokarte, Story 9.29): keine eigene Ebene, kein Fokuswechsel;
   * schließen muss der Aufrufer (beim Wegbewegen der Maus, Esc nur wenn keine Ebene darüber liegt).
   */
  passive?: boolean;
}) {
  const wrapper = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const opener = useRef<HTMLElement | null>(null);
  const id = useId();
  const phone = useScreen() === 'phone';
  const change = useRef(onOpenChange);
  const focusOnOpen = useRef(autoFocus);
  useEffect(() => {
    change.current = onOpenChange;
    focusOnOpen.current = autoFocus;
  }, [onOpenChange, autoFocus]);

  // Auslösenden Knopf merken, bevor das Blatt den Fokus übernimmt (Layout-Effekt läuft vor dessen Effekt): dorthin kehrt
  // der Fokus nach dem Schließen zurück.
  useLayoutEffect(() => {
    if (open && phone) opener.current = wrapper.current?.querySelector<HTMLElement>('button') ?? null;
  }, [open, phone]);

  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    if (!open || phone || !fixed) return;
    const update = () => {
      const anchor = wrapper.current?.getBoundingClientRect();
      if (!anchor) return;
      const width = panel.current?.offsetWidth ?? 0;
      const height = panel.current?.offsetHeight ?? 0;
      const margin = 8;
      const wanted = align === 'end' ? anchor.right - width : anchor.left;
      const left = Math.max(margin, Math.min(wanted, window.innerWidth - width - margin));
      // Passt es unten nicht, über dem Knopf; nie aus dem Fenster.
      const below = anchor.bottom + 8;
      const top = below + height > window.innerHeight - margin ? Math.max(margin, anchor.top - 8 - height) : below;
      setPlace({ top, left });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    // Inhalt wächst (z. B. Bedingung „zwischen“ zeigt von–bis): neu ausrichten, damit nichts aus dem Fenster ragt.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    if (panel.current) observer?.observe(panel.current);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
      observer?.disconnect();
    };
  }, [open, phone, fixed, align]);

  // Desktop/Tablet: eigene (nicht modale) Ebene; Esc schließt nur das oberste Popover, Fokus zurück auf den Knopf.
  useLayer(open && !phone && !passive, {
    root: wrapper,
    onEscape: () => {
      change.current(false);
      wrapper.current?.querySelector<HTMLElement>('button')?.focus();
    },
  });

  useEffect(() => {
    // Am Handy übernimmt das Blatt Fokus, Esc und Tippen daneben.
    if (!open || phone) return;
    // Fokus in das erste Feld bzw. den Dialog (nicht bei `autoFocus = false`).
    if (focusOnOpen.current) {
      const first = panel.current?.querySelector<HTMLElement>('input:not([disabled]), button:not([disabled]), select:not([disabled]), [tabindex="0"]');
      (first ?? panel.current)?.focus();
    }
    const onPointer = (e: PointerEvent) => {
      if (!wrapper.current?.contains(e.target as Node)) change.current(false);
    };
    document.addEventListener('pointerdown', onPointer);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
    };
  }, [open, phone]);

  return (
    <div ref={wrapper} className="relative">
      {trigger({
        'aria-expanded': open,
        'aria-controls': open ? id : undefined,
        onClick: () => onOpenChange(!open),
      })}
      {open && phone ? (
        <Sheet id={id} label={label} opener={opener} onClose={() => change.current(false)}>
          {children}
        </Sheet>
      ) : null}
      {open && !phone ? (
        <div
          id={id}
          ref={panel}
          role="dialog"
          aria-label={label}
          tabIndex={-1}
          className={cn(
            fixed ? 'fixed z-40 rounded-card bg-card p-space-4 text-left font-normal text-ink shadow-float' : 'absolute top-[calc(100%+8px)] z-40 rounded-card bg-card p-space-4 text-ink shadow-float',
            !fixed && (align === 'end' ? 'right-0' : 'left-0'),
            className,
          )}
          style={fixed ? (place ? { top: place.top, left: place.left } : { visibility: 'hidden', top: 0, left: 0 }) : undefined}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}
